/* eslint-disable */

import config from 'config'
import i18next from 'i18next'
import { getRequestSessionId, currentRoute } from 'utils/requestSession'

// Заголовки для склейки запросов одной вкладки и страницы в логах Контроль-API
// (lograge → Kibana, business_apps#1465). Уходят только на Контроль-API:
// SSO и сторонним хостам идентификатор вкладки не нужен.
const SESSION_HEADER = 'X-Request-Session'
const ROUTE_HEADER = 'X-Client-Route'

// Одна SSO-навигация на страницу: параллельные 401 (поллеры + запросы данных)
// не должны наперегонки дёргать window.location — гонка перетирает
// omniauth.state в общей cookie и заканчивается /auth/failure?message=csrf_detected.
let ssoRedirectStarted = false

// Возврат на страницу через bfcache («Назад» со страницы SSO/failure) восстанавливает
// JS-состояние вместе с взведённым guard'ом — сбрасываем, иначе следующий 401
// уже не уведёт на SSO до ручной перезагрузки.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) ssoRedirectStarted = false
})

// Моки новой главной (business_apps#1486): REACT_APP_HOME_V2_MOCKS=true подменяет
// ответы Контроль-API и SSO, пока бэкенд (#1487, #1488) в работе. Только для
// yarn start: NODE_ENV подставляется на сборке, и в production-бандл (прод и
// стенды) модуль моков не попадает.
let homeV2MockRequest = null
if (
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_HOME_V2_MOCKS === 'true'
) {
  homeV2MockRequest = require('features/HomeV2/model/mocks').mockRequest
}

const startSsoRedirect = () => {
  sessionStorage.removeItem('auth')
  window.location = `${config.loginUri}/${
    config.ssoPath
  }?origin=${encodeURIComponent(window.location.href)}`
}

export const redirectToSso = () => {
  if (ssoRedirectStarted) return
  ssoRedirectStarted = true

  if (document.visibilityState === 'hidden') {
    // Фоновая вкладка не уходит на SSO-круг сама: навигация в фоне выглядит как
    // самопроизвольная перезагрузка и плодит гонки между вкладками.
    document.addEventListener('visibilitychange', startSsoRedirect, {
      once: true,
    })
    return
  }

  startSsoRedirect()
}

/**
 *
 *
 * @export
 * @class Api
 */
class Api {
  /**
   * Creates an instance of Api.
   * @param {*} baseUri
   * @memberof Api
   */
  constructor(baseUri) {
    this.baseUri = baseUri
    this._actingPartnerUuid = null
  }

  setActingPartner(uuid) {
    this._actingPartnerUuid = uuid
  }

  getActingPartner() {
    return this._actingPartnerUuid
  }

  _isControlApi(baseUri) {
    return baseUri === this.baseUri || baseUri.startsWith(config.redirectUri)
  }

  /**
   * Makes a redirect to the SSO if err status is 401, returns a Promise.reject
   *
   * @param {*} err
   * @param {?Object} [opts]
   * @returns {Promise<Response>}
   * @memberof Api
   */
  _handleError(err, { skipAuthRedirect } = {}) {
    if (err && err.status === 401 && !skipAuthRedirect) {
      redirectToSso()
    }
    return Promise.reject(err)
  }

  /**
   * Makes an api request, handles error status 401
   *
   * @param {string} endpoint - the endpoint, that gets appended to the (default) baseUri
   * @param {?Object} [options={}] - custom request options, override default ones;
   *   skipAuthRedirect: true — не уводить на SSO при 401 (для фоновых поллеров)
   * @param {?string} baseUri - custom baseUri
   * @returns {Promise<any>}
   * @memberof Api
   */
  // endpoint: string, options: ?Object = {}, baseUri: ?string, blob
  request(endpoint, options, baseUri, blob) {
    const { skipAuthRedirect, ...fetchOptions } = options || {}
    const BASE_URI = baseUri || this.baseUri
    if (homeV2MockRequest && !blob) {
      const mocked = homeV2MockRequest(endpoint, fetchOptions, BASE_URI)
      if (mocked) return mocked
    }
    const selectedLanguage = i18next.language
    const headers = {
      'Content-Type': 'application/json',
      'Accept-Language': selectedLanguage,
    }
    if (this._actingPartnerUuid) {
      headers['X-Acting-Service-Partner-Uuid'] = this._actingPartnerUuid
    }
    if (this._isControlApi(BASE_URI)) {
      const sessionId = getRequestSessionId()
      const route = currentRoute()
      if (sessionId) headers[SESSION_HEADER] = sessionId
      if (route) headers[ROUTE_HEADER] = route
    }
    const request = new Request(`${BASE_URI}${endpoint}`, {
      headers: new Headers(headers),
      credentials: 'include',
      ...fetchOptions,
    })
    return fetch(request)
      .then((r) =>
        blob ? r.blob() : r.status <= 207 ? r.json() : Promise.reject(r)
      )
      .catch((err) => this._handleError(err, { skipAuthRedirect }))
  }
}

const api = new Api(config.baseUri)

export default api
